// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Lakko Contributors

/**
 * Call sound cues for the web client (S-3-A-sounds).
 *
 * Sound files live in `apps/web/public/sounds/*.m4a` — they are
 * generated by `tools/audio/synth-sounds.sh` from pure sine
 * tones (no third-party samples). The browser serves them
 * directly from the public folder; we instantiate one
 * `HTMLAudioElement` per cue and reuse it across plays.
 *
 * Two play modes:
 *
 * - `playOnce(name)` — fire-and-forget short cue
 *   (`join` / `leave` / `error`). Each call rewinds the cached
 *   element to the start so rapid repeated plays do not
 *   silently drop on browsers that refuse `play()` on an
 *   already-playing element.
 *
 * - `startLoop(name)` / `stopLoop(name)` — repeating cue
 *   (`ringtone-incoming` / `ringback-outgoing`). The element
 *   carries `loop = true`; stopping pauses + rewinds so the
 *   next start begins cleanly at the burst boundary.
 *
 * Browser autoplay policy: the first `play()` call after a
 * user gesture (the join button) is granted unconditionally;
 * subsequent calls inherit that grant on the same page load.
 * We rely on the join button as the gesture-of-record.
 *
 * No analytics, no observability hooks — these are user-
 * perceptible cues; if they fail to play, the user will
 * notice. We log via `console.warn` so a debugger can pick
 * up failures without spamming production telemetry.
 */

/**
 * Pinned set of cue names. Same string identifiers used by the
 * iOS and Android controllers — keeping the names in lockstep
 * lets the wiring layer share a single trigger map across
 * platforms.
 */
export type SoundName =
  | "ringtone-incoming"
  | "ringback-outgoing"
  | "join"
  | "leave"
  | "error";

/**
 * Cues that loop until explicitly stopped. Everything else is
 * fire-and-forget.
 */
const LOOPING_CUES: ReadonlySet<SoundName> = new Set([
  "ringtone-incoming",
  "ringback-outgoing",
]);

/**
 * Resolve a cue name to a public URL the browser fetches.
 * Defaults to `/sounds/<name>.m4a` — adjust here if the deploy
 * path ever changes (e.g. CDN prefix).
 */
function cueUrl(name: SoundName): string {
  return `/sounds/${name}.m4a`;
}

/**
 * Per-cue cached `HTMLAudioElement`. Reusing the element keeps
 * the audio decoder warm and skips a fresh network round-trip
 * for every `playOnce`.
 */
type CueCache = Partial<Record<SoundName, HTMLAudioElement>>;

/**
 * Sound controller singleton. The web client constructs one
 * per session — see `apps/web/src/routes/Call.tsx` for the
 * wire-up. Disposing the controller (call ended, route
 * changed) stops every active loop and zeroes the cache.
 */
/** Every live controller, so a page that is going away silences them all. */
const LIVE_CONTROLLERS = new Set<CallSoundController>();

/**
 * Seen live 2026-10-29: the outgoing tone started on the sign-in page of a
 * Safari tab at the moment the person closed it, with no call in that tab.
 * The cause is not known. Whatever started it, nothing of a call may sound
 * from a page that is being left, so `pagehide` stops every cue. (Not
 * `visibilitychange`: an incoming call must still ring in a background tab.)
 */
if (typeof globalThis.addEventListener === "function") {
  globalThis.addEventListener("pagehide", () => {
    for (const controller of LIVE_CONTROLLERS) controller.stopAll();
  });
}

export class CallSoundController {
  private readonly cache: CueCache = {};

  /**
   * Construct the controller. `volume` is an optional override
   * (0.0 .. 1.0) applied uniformly to every cue; defaults to
   * 1.0 (the AAC encoding already targets a comfortable −10 dB
   * peak, so the OS volume slider is the user-facing knob).
   */
  public constructor(private readonly volume: number = 1.0) {
    LIVE_CONTROLLERS.add(this);
  }

  /**
   * Play `name` once. Repeated `playOnce(name)` calls rewind
   * the cached element so multiple peer-join events in a
   * burst each surface as audible cues.
   */
  public playOnce(name: SoundName): void {
    const audio = this.element(name);
    audio.currentTime = 0;
    audio.loop = false;
    audio.volume = this.volume;
    void audio.play().catch((err) => {
      console.warn(`[call-sounds] playOnce("${name}") failed`, err);
    });
  }

  /**
   * Start `name` looping. If the same cue is already looping
   * this is a no-op — we do not restart from zero, so the
   * pattern (e.g. 2 s on / 4 s off) stays steady.
   */
  public startLoop(name: SoundName): void {
    if (!LOOPING_CUES.has(name)) {
      console.warn(
        `[call-sounds] startLoop("${name}") called on a non-looping cue; ignoring`,
      );
      return;
    }
    const audio = this.element(name);
    if (!audio.paused) {
      return;
    }
    audio.loop = true;
    audio.volume = this.volume;
    // One plain line per loop start, so a tone nobody expected can be traced
    // in the console (the 2026-10-29 sign-in page case).
    console.info(`[call-sounds] loop started: ${name}`);
    void audio.play().catch((err) => {
      console.warn(`[call-sounds] startLoop("${name}") failed`, err);
    });
  }

  /**
   * Stop `name`'s loop, rewinding so a subsequent `startLoop`
   * starts cleanly at the burst boundary rather than mid-tone.
   */
  public stopLoop(name: SoundName): void {
    const audio = this.cache[name];
    if (!audio) {
      return;
    }
    audio.pause();
    audio.currentTime = 0;
  }

  /**
   * Stop every active cue (looping or one-shot). Called on
   * call-end / route-change / page-unload.
   */
  public stopAll(): void {
    for (const audio of Object.values(this.cache)) {
      if (audio === undefined) {
        continue;
      }
      audio.pause();
      audio.currentTime = 0;
    }
  }

  /**
   * Free every cached element. Call from a route-cleanup
   * (`onCleanup` in Solid) — without this the elements stay
   * alive for the lifetime of the page.
   */
  public dispose(): void {
    LIVE_CONTROLLERS.delete(this);
    this.stopAll();
    for (const key of Object.keys(this.cache) as SoundName[]) {
      const audio = this.cache[key];
      if (audio !== undefined) {
        audio.src = "";
        audio.load();
        delete this.cache[key];
      }
    }
  }

  private element(name: SoundName): HTMLAudioElement {
    let audio = this.cache[name];
    if (audio === undefined) {
      audio = new Audio(cueUrl(name));
      // `preload="auto"` is the spec default; spelled out so
      // a future minifier that strips defaults can't change
      // it accidentally.
      audio.preload = "auto";
      this.cache[name] = audio;
    }
    return audio;
  }
}
